import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Icon from '../components/Icon';
import AdminShell from '../components/admin/AdminShell';
import { formatDateWithTime } from '../utils/format';
import { SkeletonAdminBody } from '../components/Skeleton';

const STATUS_OPTIONS = ['all', 'open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-200 text-gray-700',
};

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-sky-100 text-sky-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

function AdminSupportTicketsPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [savingId, setSavingId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [drafts, setDrafts] = useState({});

  const actionableCount = useMemo(
    () => tickets.filter((ticket) => ['open', 'in_progress', 'waiting_on_user'].includes(ticket.status)).length,
    [tickets]
  );
  const unreadCount = useMemo(() => tickets.filter((ticket) => ticket.unreadForAdmin).length, [tickets]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket._id === selectedTicketId) || null,
    [tickets, selectedTicketId]
  );

  const seedDraft = (ticket) => ({
    status: ticket.status || 'open',
    priority: ticket.priority || 'medium',
    message: '',
    assignToSelf: false,
  });

  const syncDrafts = (nextTickets) => {
    setDrafts((prev) => {
      const next = {};
      nextTickets.forEach((ticket) => {
        next[ticket._id] = prev[ticket._id]
          ? {
              ...prev[ticket._id],
              status: ticket.status || prev[ticket._id].status || 'open',
              priority: ticket.priority || prev[ticket._id].priority || 'medium',
            }
          : seedDraft(ticket);
      });
      return next;
    });
  };

  const loadPage = async (status = filterStatus) => {
    try {
      setLoading(true);
      const [userRes, ticketsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get(`/auth/support-tickets/admin${status && status !== 'all' ? `?status=${status}` : ''}`),
      ]);

      const userData = userRes?.data;
      if (!userData?._id) {
        navigate('/login');
        return;
      }
      if (userData.role !== 'A') {
        navigate('/dashboard');
        return;
      }

      setCurrentUser(userData);

      const nextTickets = Array.isArray(ticketsRes?.data?.tickets) ? ticketsRes.data.tickets : [];
      setTickets(nextTickets);
      syncDrafts(nextTickets);
      setSelectedTicketId((prev) => {
        if (nextTickets.some((ticket) => ticket._id === prev)) return prev;
        return nextTickets[0]?._id || null;
      });
      setError('');
      await api.post('/auth/support-tickets/mark-admin-viewed');
      setTickets((prev) => prev.map((ticket) => ({ ...ticket, unreadForAdmin: false })));
    } catch (fetchError) {
      console.error('Failed to load admin support tickets:', fetchError);
      if (fetchError?.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(fetchError?.response?.data?.error || 'Failed to load support ticket queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    loadPage('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleDraftChange = (ticketId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdateTicket = async (ticketId) => {
    const draft = drafts[ticketId] || {};
    try {
      setSavingId(ticketId);
      const response = await api.patch(`/auth/support-tickets/${ticketId}/admin`, draft);
      const updatedTicket = response?.data?.ticket;

      setTickets((prev) => prev.map((ticket) => (ticket._id === ticketId ? updatedTicket : ticket)));
      setDrafts((prev) => ({
        ...prev,
        [ticketId]: {
          status: updatedTicket?.status || draft.status || 'open',
          priority: updatedTicket?.priority || draft.priority || 'medium',
          message: '',
          assignToSelf: false,
        },
      }));
      setMessage('Ticket updated successfully.');
      setError('');
    } catch (saveError) {
      console.error('Failed to update ticket:', saveError);
      setError(saveError?.response?.data?.error || 'Failed to update support ticket.');
      setMessage('');
    } finally {
      setSavingId(null);
    }
  };

  const handleFilterChange = async (nextStatus) => {
    setFilterStatus(nextStatus);
    await loadPage(nextStatus);
  };

  if (loading && !currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <SkeletonAdminBody rows={6} />
      </div>
    );
  }

  if (!currentUser) return null;

  return (
    <AdminShell
      user={currentUser}
      title="Support Tickets"
      subtitle="Review and resolve user support requests"
      maxWidth="max-w-7xl"
    >
      <div className="space-y-6">
        <section className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Support Queue</h1>
              <p className="mt-1 text-sm text-gray-600">
                Browse compact ticket cards, then open one ticket to continue the full conversation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Actionable</div>
                <div className="mt-1 text-lg font-bold text-emerald-900">{actionableCount}</div>
              </div>
              <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">Unread</div>
                <div className="mt-1 text-lg font-bold text-sky-900">{unreadCount}</div>
              </div>
              <button
                onClick={() => loadPage(filterStatus)}
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icon name="arrow-clockwise" size="1rem" />
                Refresh
              </button>
            </div>
          </div>
        </section>

        {message ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <section className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-sm">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  filterStatus === status
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'All' : status.replaceAll('_', ' ')}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Refreshing ticket queue...</div>
          ) : tickets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500">
              No tickets match the current filter.
            </div>
          ) : (
            <div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
              <div className="space-y-3">
                {tickets.map((ticket) => {
                  const isSelected = selectedTicketId === ticket._id;
                  const latestMessage = Array.isArray(ticket.messages) && ticket.messages.length > 0
                    ? ticket.messages[ticket.messages.length - 1]
                    : null;

                  return (
                    <button
                      key={ticket._id}
                      type="button"
                      onClick={() => setSelectedTicketId(ticket._id)}
                      className={`w-full rounded-3xl border p-4 text-left shadow-sm transition ${
                        isSelected
                          ? 'border-emerald-200 bg-emerald-50/80'
                          : 'border-gray-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="truncate text-sm font-semibold text-gray-900">{ticket.subject}</h2>
                            {ticket.unreadForAdmin ? (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                New
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-[11px] text-gray-500">
                            {ticket.ticketNumber} · {ticket.createdBy?.name || 'Unknown user'}
                          </div>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${STATUS_STYLES[ticket.status] || STATUS_STYLES.open}`}>
                          {ticket.status.replaceAll('_', ' ')}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium}`}>
                          {ticket.priority}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-700">
                          {ticket.category}
                        </span>
                      </div>

                      <p className="mt-3 text-xs leading-5 text-gray-600">
                        {latestMessage?.message || ticket.description}
                      </p>

                      <div className="mt-3 text-[11px] text-gray-500">
                        {formatDateWithTime(ticket.latestMessageAt || ticket.updatedAt || ticket.createdAt)}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedTicket ? (
                <article className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold text-gray-900">{selectedTicket.subject}</h2>
                        {selectedTicket.unreadForAdmin ? (
                          <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold text-red-700">
                            New
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                        <span>{selectedTicket.ticketNumber}</span>
                        <span>{selectedTicket.category}</span>
                        <span>{formatDateWithTime(selectedTicket.createdAt)}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLES[selectedTicket.status] || STATUS_STYLES.open}`}>
                          {selectedTicket.status.replaceAll('_', ' ')}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${PRIORITY_STYLES[selectedTicket.priority] || PRIORITY_STYLES.medium}`}>
                          {selectedTicket.priority}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700">
                          {selectedTicket.createdBy?.name || 'Unknown user'}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
                      <div>User: {selectedTicket.createdBy?.email || 'No email'}</div>
                      <div>Phone: {selectedTicket.createdBy?.phone || 'No phone'}</div>
                      <div>Assigned: {selectedTicket.assignedTo?.name || 'Unassigned'}</div>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                        Conversation
                      </div>
                      <div className="max-h-[620px] space-y-3 overflow-y-auto rounded-3xl border border-gray-200 bg-gray-50 p-4">
                        {(selectedTicket.messages || []).map((entry, index) => {
                          const isAdmin = entry.senderType === 'admin';
                          return (
                            <div
                              key={entry._id || `${selectedTicket._id}-${index}`}
                              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[90%] rounded-3xl px-4 py-3 text-sm shadow-sm ${
                                  isAdmin
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-white text-gray-800 border border-gray-200'
                                }`}
                              >
                                <div className={`text-[11px] font-semibold ${isAdmin ? 'text-emerald-100' : 'text-gray-500'}`}>
                                  {isAdmin ? (entry.sender?.name || 'Admin') : (entry.sender?.name || 'User')}
                                </div>
                                <p className="mt-1 whitespace-pre-wrap leading-6">{entry.message}</p>
                                <div className={`mt-2 text-[10px] ${isAdmin ? 'text-emerald-100' : 'text-gray-400'}`}>
                                  {formatDateWithTime(entry.createdAt)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                        Update Ticket
                      </div>
                      <div className="mt-3 space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
                            <select
                              value={(drafts[selectedTicket._id] || seedDraft(selectedTicket)).status}
                              onChange={(event) => handleDraftChange(selectedTicket._id, 'status', event.target.value)}
                              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              {STATUS_OPTIONS.filter((option) => option !== 'all').map((option) => (
                                <option key={option} value={option}>
                                  {option.replaceAll('_', ' ')}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">Priority</label>
                            <select
                              value={(drafts[selectedTicket._id] || seedDraft(selectedTicket)).priority}
                              onChange={(event) => handleDraftChange(selectedTicket._id, 'priority', event.target.value)}
                              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              {PRIORITY_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Reply to user</label>
                          <textarea
                            value={(drafts[selectedTicket._id] || seedDraft(selectedTicket)).message}
                            onChange={(event) => handleDraftChange(selectedTicket._id, 'message', event.target.value)}
                            rows={7}
                            className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Write a reply or resolution update. It will appear in the ticket conversation."
                          />
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={(drafts[selectedTicket._id] || seedDraft(selectedTicket)).assignToSelf === true}
                            onChange={(event) => handleDraftChange(selectedTicket._id, 'assignToSelf', event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          Assign this ticket to me
                        </label>

                        <button
                          onClick={() => handleUpdateTicket(selectedTicket._id)}
                          disabled={savingId === selectedTicket._id}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                        >
                          <Icon name="save" size="1rem" />
                          {savingId === selectedTicket._id ? 'Saving...' : 'Save Update'}
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ) : (
                <div className="rounded-3xl border border-dashed border-gray-300 bg-gray-50 px-5 py-12 text-center text-sm text-gray-500">
                  Select a ticket card to open the full conversation.
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}

export default AdminSupportTicketsPage;
